import cx from "classnames";
import { Alert } from "components/common/Alert/Alert";
import { useTranslationContext } from "context/translation";
import { asyncFieldLevelMemo } from "helpers/validation";
import { FC, useCallback, useState } from "react";
import { Field } from "react-final-form";
import { FieldInputProps } from "..";
import st from "../FieldInput.module.scss";
import { PasswordRequirements } from "./PasswordRequirements/PasswordRequirements";

// copied from v2 schema (does not currently exist in v3)
export type PasswordRequirement = {
  __typename?: "PasswordRequirement";
  msg: string;
  satisfied: boolean;
};

interface PasswordInputProps<T = string> extends FieldInputProps<T> {
  handlePasswordChange: (password: string) => Promise<PasswordRequirement[]>;
  placeholder?: string;
}

export const PasswordInput: FC<PasswordInputProps> = ({
  name,
  defaultValue,
  autocompleteValue,
  browserAutofillOffValue,
  maxLength,
  inputClassName,
  placeholder,
  label,
  onClick,
  handlePasswordChange,
  ...inputProps
}) => {
  const [passwordRequirements, setPasswordRequirements] = useState<
    PasswordRequirement[]
  >([]);

  const { invalidPassword } = useTranslationContext();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleValidation = useCallback(
    asyncFieldLevelMemo(async (value: string) => {
      const passwordReqs = await handlePasswordChange(value);
      setPasswordRequirements(passwordReqs);
      const incompletePasswordReqs = passwordReqs.some(
        (req) => req.satisfied === false,
      );
      if (incompletePasswordReqs) {
        return invalidPassword;
      }
      return;
    }),
    [],
  );

  return (
    <Field
      name={name}
      defaultValue={defaultValue}
      {...inputProps}
      validate={handleValidation}
    >
      {({ input, meta }) => {
        const hasError = meta.touched && meta.error;

        return (
          <div className={cx(st.inputContainer, inputClassName)}>
            {label && (
              <label htmlFor={name} className={st.label}>
                {label}
              </label>
            )}
            <input
              id={name}
              type="text"
              {...input}
              value={input.value && input.value.toString()}
              placeholder={placeholder}
              autoComplete={browserAutofillOffValue}
              maxLength={maxLength}
              onClick={onClick}
              className={cx(st.fieldInput, hasError && st.error)}
            />
            {hasError && (
              <Alert hideBackground type="error">
                {meta.error}
              </Alert>
            )}
            {meta.active && (
              <PasswordRequirements
                hasEnteredPassword={!!input.value}
                passwordRequirements={passwordRequirements}
              />
            )}
          </div>
        );
      }}
    </Field>
  );
};
